// ReturnUserBriefing.tsx - Personalized briefing for returning users
// Shows what happened while they were away

import { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, Briefcase, Newspaper, Lightbulb, Eye } from 'lucide-react';
import { shouldShowReturnBriefing, loadProfile } from '../services/personalizationService';
import './ReturnUserBriefing.css';

interface BriefingData {
  marketMove: {
    ticker: string;
    change_pct: number;
    direction: 'up' | 'down';
  };
  watchlistMove: {
    avg_change_pct: number;
    direction: 'up' | 'down';
  };
  portfolioMove?: {
    change_amount: number;
    change_pct: number;
    direction: 'up' | 'down';
    top_gainer?: string;
  };
  keyEvents: string[];
  opportunities: {
    count: number;
    top_ticker?: string;
    top_score?: number;
  };
}

interface ReturnUserBriefingProps {
  // Data can be passed in or will use defaults
  data?: Partial<BriefingData>;
  onDismiss?: () => void;
}

// Default/sample data when real data isn't available
const DEFAULT_BRIEFING: BriefingData = {
  marketMove: { ticker: 'SPY', change_pct: 0.87, direction: 'up' },
  watchlistMove: { avg_change_pct: 1.24, direction: 'up' },
  portfolioMove: {
    change_amount: 342.56,
    change_pct: 1.45,
    direction: 'up',
    top_gainer: 'NVDA'
  },
  keyEvents: [
    '2 earnings reports released',
    'Fed statement: rates unchanged'
  ],
  opportunities: {
    count: 3,
    top_ticker: 'AGX',
    top_score: 91
  }
};

export function ReturnUserBriefing({ data, onDismiss }: ReturnUserBriefingProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [briefingData, setBriefingData] = useState<BriefingData>(DEFAULT_BRIEFING);

  useEffect(() => {
    // Check if we should show the briefing
    const shouldShow = shouldShowReturnBriefing();
    const alreadyShown = sessionStorage.getItem('return_briefing_shown');

    if (shouldShow && !alreadyShown) {
      // Merge passed data with defaults
      if (data) {
        setBriefingData({ ...DEFAULT_BRIEFING, ...data });
      }

      setIsVisible(true);
      sessionStorage.setItem('return_briefing_shown', 'true');

      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => {
        handleDismiss();
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [data]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  const profile = loadProfile();
  const { marketMove, watchlistMove, portfolioMove, keyEvents, opportunities } = briefingData;

  return (
    <div className="return-briefing-overlay" onClick={handleDismiss}>
      <div className="return-briefing" onClick={e => e.stopPropagation()}>
        <button className="briefing-close" onClick={handleDismiss} aria-label="Close briefing">
          <X size={18} />
        </button>

        <div className="briefing-header">
          <Eye size={24} className="briefing-icon" />
          <div>
            <h2>Welcome back, Justin.</h2>
            <p>Here's what happened while you were away:</p>
          </div>
        </div>

        <div className="briefing-sections">
          {/* Market Moves */}
          <div className="briefing-section">
            <div className="section-header">
              {marketMove.direction === 'up' ? (
                <TrendingUp size={18} className="up" />
              ) : (
                <TrendingDown size={18} className="down" />
              )}
              <span>Market Moves</span>
            </div>
            <p className="section-content">
              <strong>{marketMove.ticker}</strong> is{' '}
              <span className={marketMove.direction}>
                {marketMove.direction} {Math.abs(marketMove.change_pct).toFixed(2)}%
              </span>{' '}
              since you last visited.
              {profile.most_viewed_tickers.length > 0 && (
                <> Your watchlist is{' '}
                <span className={watchlistMove.direction}>
                  {watchlistMove.direction} an average of {Math.abs(watchlistMove.avg_change_pct).toFixed(2)}%
                </span>.
                </>
              )}
            </p>
          </div>

          {/* Portfolio (if they have one) */}
          {portfolioMove && (
            <div className="briefing-section">
              <div className="section-header">
                <Briefcase size={18} />
                <span>Your Holdings</span>
              </div>
              <p className="section-content">
                Your portfolio is{' '}
                <span className={portfolioMove.direction}>
                  {portfolioMove.direction} ${Math.abs(portfolioMove.change_amount).toFixed(2)} ({Math.abs(portfolioMove.change_pct).toFixed(2)}%)
                </span>{' '}
                since your last visit.
                {portfolioMove.top_gainer && (
                  <> <strong>{portfolioMove.top_gainer}</strong> gained the most.</>
                )}
              </p>
            </div>
          )}

          {/* Key Events */}
          {keyEvents.length > 0 && (
            <div className="briefing-section">
              <div className="section-header">
                <Newspaper size={18} />
                <span>Key Events</span>
              </div>
              <p className="section-content">
                {keyEvents.join('. ')}.
              </p>
            </div>
          )}

          {/* Opportunities */}
          {opportunities.count > 0 && (
            <div className="briefing-section highlight">
              <div className="section-header">
                <Lightbulb size={18} />
                <span>Opportunities</span>
              </div>
              <p className="section-content">
                Cipher identified <strong>{opportunities.count} new high-score opportunities</strong> matching your interests.
                {opportunities.top_ticker && opportunities.top_score && (
                  <> <strong>{opportunities.top_ticker}</strong> has a Cipher Score of <strong>{opportunities.top_score}</strong> and may be worth researching.</>
                )}
              </p>
            </div>
          )}
        </div>

        <div className="briefing-footer">
          <span>Click anywhere to continue</span>
        </div>
      </div>
    </div>
  );
}

export default ReturnUserBriefing;
